import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  UserRound, Building2, FileText, Award, Camera, Save,
  Check, X, Search, Grid as GridIcon,
  AtSign, Video, Globe, Link2
} from "lucide-react";
import i18n from "../i18n";
import { BackButton } from "../components/BackButton";


/* ========= i18n mini-hook ========= */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

/* ========= Types ========= */
type Team = {
  id: string;
  name: string;
  league?: string | null;
  slug: string;
  logo_path: string;
};

type Socials = {
  instagram: string;
  twitter: string;
  youtube: string;
  tiktok: string;
  website: string;
};

type UserProfileForm = {
  full_name: string;
  bio: string;
  club: string;
  photo_url: string;
  niveau: string;
  achievements: string;
  socials: Socials;
  pseudo: string;
  public_use_pseudo: boolean;
};

/* ========= Helpers ========= */
const publicLogoUrl = (logoPath: string) => {
  if (logoPath.startsWith("/")) return logoPath;
  if (logoPath.includes("/")) {
    const [bucket, ...rest] = logoPath.split("/");
    const key = rest.join("/");
    return supabase.storage.from(bucket).getPublicUrl(key).data.publicUrl;
  }
  return supabase.storage.from("team-logos").getPublicUrl(logoPath).data.publicUrl;
};

const defaultSocials: Socials = {
  instagram: "",
  twitter: "",
  youtube: "",
  tiktok: "",
  website: "",
};

const ensureHttps = (v: string) => {
  const s = (v || "").trim();
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

/* ========= Modale de sélection ========= */
function FavoriteTeamsModal({
  open,
  onClose,
  teams,
  logoUrls,
  initialSelected,
  onApply,
  t,
}: {
  open: boolean;
  onClose: () => void;
  teams: Team[];
  logoUrls: Record<string, string>;
  initialSelected: string[];
  onApply: (ids: string[]) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const [localSelected, setLocalSelected] = useState<string[]>(initialSelected);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) {
      setLocalSelected(initialSelected);
      setQ("");
    }
  }, [open, initialSelected]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return teams;
    return teams.filter(
      (t) =>
        t.name.toLowerCase().includes(s) ||
        (t.league || "").toLowerCase().includes(s) ||
        t.slug.toLowerCase().includes(s)
    );
  }, [q, teams]);

  const toggle = (id: string) => {
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setLocalSelected(filtered.map((t) => t.id));
  const clearAll = () => setLocalSelected([]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 top-10 mx-auto w-[92vw] max-w-5xl rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <GridIcon className="w-5 h-5 text-gray-500" />
            <h3 className="font-semibold">{t("editprofile:modal.title")}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800" aria-label={t("editprofile:modal.close")}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Barre de recherche + actions */}
        <div className="px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("editprofile:modal.search_placeholder")}
              className="w-full rounded-lg border px-3 py-2 pl-9 bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={selectAll} className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
              {t("editprofile:modal.select_all")}
            </button>
            <button onClick={clearAll} className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
              {t("editprofile:modal.clear_all")}
            </button>
          </div>
        </div>

        {/* Grille logos */}
        <div className="px-4 pb-4 max-h-[60vh] overflow-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">{t("editprofile:modal.empty")}</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {filtered.map((team) => {
                const active = localSelected.includes(team.id);
                return (
                  <button
                    key={team.id}
                    type="button"
                    onClick={() => toggle(team.id)}
                    className={`relative rounded-xl border p-3 flex flex-col items-center gap-2 hover:shadow transition
                      ${active ? "ring-2 ring-blue-500 border-blue-500" : "border-gray-300 dark:border-gray-700"}`}
                    aria-pressed={active}
                  >
                    <div className="w-16 h-16 overflow-hidden rounded-lg bg-white flex items-center justify-center">
                      <img
                        src={logoUrls[team.id] || ""}
                        alt={team.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium">{team.name}</div>
                      {team.league && <div className="text-xs text-gray-500">{team.league}</div>}
                    </div>
                    {active && (
                      <div className="absolute top-2 right-2 bg-blue-600 text-white rounded-full p-1">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
            {t("editprofile:modal.cancel")}
          </button>
          <button
            onClick={() => onApply(localSelected)}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            <Check className="w-4 h-4" />
            {t("editprofile:modal.apply_with_count", { count: localSelected.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========= Page ========= */
export const EditProfile = () => {
  const { t } = useI18nSafe();

  const [profile, setProfile] = useState<UserProfileForm>({
    full_name: "",
    bio: "",
    club: "",
    photo_url: "",
    niveau: "",
    achievements: "",
    socials: { ...defaultSocials },
    pseudo: "",
    public_use_pseudo: false,
  });

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [signedUrl, setSignedUrl] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pseudoErr, setPseudoErr] = useState<string>("");

  // Équipes préférées
  const [teams, setTeams] = useState<Team[]>([]);
  const [logoUrls, setLogoUrls] = useState<Record<string, string>>({});
  const [favoriteTeamIds, setFavoriteTeamIds] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const navigate = useNavigate();

  const isValidPseudo = (v: string) => /^[A-Za-z0-9_.-]{3,32}$/.test(v);

  useEffect(() => {
    const fetchAll = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // Profil
      const { data: prof } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (prof) {
        setProfile({
          full_name: prof.full_name || "",
          bio: prof.bio || "",
          club: prof.club || "",
          photo_url: prof.photo_url || "",
          niveau: prof.niveau || "",
          achievements: prof.achievements || "",
          socials: { ...defaultSocials, ...(prof.socials || {}) },
          pseudo: prof.pseudo || "",
          public_use_pseudo: !!prof.public_use_pseudo,
        });
        if (prof.photo_url) {
          const { data: urlData } = await supabase.storage
            .from("avatars")
            .createSignedUrl(prof.photo_url, 3600);
          setSignedUrl(urlData?.signedUrl || "");
        }
      }

      // Catalogue pour modale
      const { data: catalog } = await supabase
        .from("teams_catalog")
        .select("id,name,league,slug,logo_path")
        .order("name", { ascending: true });

      setTeams(catalog || []);
      if (catalog && catalog.length) {
        const entries = catalog.map((t) => [t.id, publicLogoUrl(t.logo_path)] as const);
        setLogoUrls(Object.fromEntries(entries));
      }

      // Favoris existants
      const { data: favs } = await supabase
        .from("user_favorite_teams")
        .select("team_id")
        .eq("user_id", user.id);

      setFavoriteTeamIds((favs || []).map((f: any) => f.team_id));

      setLoading(false);
    };
    fetchAll();
  }, []);

  const syncFavorites = async (userId: string) => {
    const { data: existing, error: favErr } = await supabase
      .from("user_favorite_teams")
      .select("team_id")
      .eq("user_id", userId);

    if (favErr) throw favErr;

    const existingArr: string[] = (existing || []).map((x: any) => String(x.team_id));
    const desiredArr: string[] = favoriteTeamIds;

    const toInsert = desiredArr.filter((id) => !existingArr.includes(id));
    const toDelete = existingArr.filter((id) => !desiredArr.includes(id));

    if (toDelete.length) {
      await supabase
        .from("user_favorite_teams")
        .delete()
        .eq("user_id", userId)
        .in("team_id", toDelete);
    }

    if (toInsert.length) {
      await supabase
        .from("user_favorite_teams")
        .insert(toInsert.map((team_id) => ({ user_id: userId, team_id })));
    }
  };

  const handleSave = async () => {
    setPseudoErr("");

    if (profile.public_use_pseudo) {
      if (!profile.pseudo.trim()) {
        setPseudoErr(t("editprofile:errors.pseudo_required"));
        return;
      }
      if (!isValidPseudo(profile.pseudo.trim())) {
        setPseudoErr(t("editprofile:errors.pseudo_format"));
        return;
      }
    } else {
      if (profile.pseudo && !isValidPseudo(profile.pseudo.trim())) {
        setPseudoErr(t("editprofile:errors.pseudo_format"));
        return;
      }
    }

    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    let photo_url = profile.photo_url;

    // Upload éventuel d'une nouvelle photo
    if (photoFile) {
      const fileExt = photoFile.name.split(".").pop();
      const filePath = `${user.id}/avatar.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, photoFile, { upsert: true });

      if (!uploadError) {
        photo_url = filePath;
        const { data: urlData } = await supabase.storage
          .from("avatars")
          .createSignedUrl(photo_url, 3600);
        setSignedUrl(urlData?.signedUrl || "");
      } else {
        console.error(uploadError);
        alert(t("editprofile:errors.photo_upload"));
      }
    }

    // Nettoyage des URLs sociales
    const socialsClean: Socials = {
      instagram: ensureHttps(profile.socials.instagram),
      twitter: ensureHttps(profile.socials.twitter),
      youtube: ensureHttps(profile.socials.youtube),
      tiktok: ensureHttps(profile.socials.tiktok),
      website: ensureHttps(profile.socials.website),
    };

    // Sauvegarde profil
    const { error } = await supabase.from("user_profiles").upsert({
      id: user.id,
      full_name: profile.full_name.trim(),
      bio: profile.bio,
      club: profile.club.trim(),
      niveau: profile.niveau,
      achievements: profile.achievements,
      photo_url,
      socials: socialsClean,
      pseudo: profile.pseudo.trim() || null,
      public_use_pseudo: profile.public_use_pseudo,
    });

    if (error) {
      console.error(error);
      if ((error as any).code === "23505" && String((error as any).message || "").toLowerCase().includes("pseudo")) {
        setPseudoErr(t("editprofile:errors.pseudo_taken"));
      } else {
        alert(t("editprofile:errors.save_profile"));
      }
      setSaving(false);
      return;
    }

    // Synchro favoris
    try {
      await syncFavorites(user.id);
    } catch (e) {
      console.error(e);
      alert(t("editprofile:errors.sync_favorites"));
      setSaving(false);
      return;
    }

    setSaving(false);
    navigate("/profile");
  };

  if (loading) {
    return (
      <p className="text-center mt-10 text-gray-600 dark:text-gray-300">
        {t("common:loading")}
      </p>
    );
  }

  /* Aperçu chips */
  const selectedTeams = teams.filter((t) => favoriteTeamIds.includes(t.id));
  const publicDisplayName = profile.public_use_pseudo && profile.pseudo
    ? profile.pseudo
    : (profile.full_name || "—");

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <div className="max-w-4xl mx-auto">
        {/* ✅ Top bar */}
        <div className="sticky top-0 z-40 -mx-6 mb-4 border-b border-gray-200 bg-gray-50/95 px-6 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] backdrop-blur dark:border-gray-800 dark:bg-gray-950/80">
          <div className="flex items-center justify-between">
            <BackButton fallback="/profile" />
            <div />
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-6 text-center text-orange-600 dark:text-orange-400">
          {t("editprofile:title")}
        </h1>

        <div className="rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
          {/* Photo / Preview + aperçu du nom public */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-800 flex items-center justify-center">
              {photoFile ? (
                <img
                  src={URL.createObjectURL(photoFile)}
                  alt={t("editprofile:avatar.preview_alt")}
                  className="w-full h-full object-cover"
                />
              ) : signedUrl ? (
                <img src={signedUrl} alt={t("editprofile:avatar.current_alt")} className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-8 h-8 text-gray-500" aria-hidden="true" />
              )}
            </div>

            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">{t("editprofile:avatar.public_name_preview_label")}</p>
              <p className="text-lg font-semibold">{publicDisplayName}</p>
              <label className="mt-2 inline-flex items-center gap-2 cursor-pointer px-3 py-2 rounded border
                               bg-white border-gray-300 text-gray-800 hover:bg-gray-50
                               dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-700">
                <Camera className="w-4 h-4" />
                <span>{t("editprofile:avatar.change_photo")}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPhotoFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* PSEUDO (public) */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.pseudo.label")}
            </label>
            <div className="relative">
              <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
              <input
                type="text"
                value={profile.pseudo}
                onChange={(e) => setProfile({ ...profile, pseudo: e.target.value })}
                className={`w-full rounded px-3 py-2 pl-9 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400
                           ${pseudoErr ? "border-red-500 focus:ring-red-400/60" : ""}`}
                placeholder={t("editprofile:fields.pseudo.placeholder")}
                maxLength={32}
              />
            </div>
            <div className="mt-1 flex items-center justify-between text-xs">
              <span className={` ${pseudoErr ? "text-red-600" : "text-gray-500 dark:text-gray-400"}`}>
                {t("editprofile:fields.pseudo.hint")}
              </span>
              <span className="text-gray-400">{profile.pseudo.length}/32</span>
            </div>
            {pseudoErr && <p className="mt-1 text-xs text-red-600">{pseudoErr}</p>}
          </div>

          {/* Toggle affichage public = pseudo */}
          <div className="mb-6">
            <label className="inline-flex items-center gap-3">
              <input
                type="checkbox"
                checked={profile.public_use_pseudo}
                onChange={(e) => setProfile({ ...profile, public_use_pseudo: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
              />
              <span className="text-sm" dangerouslySetInnerHTML={{ __html: t("editprofile:fields.public_use_pseudo.label_html") }} />
            </label>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("editprofile:fields.public_use_pseudo.preview", { name: publicDisplayName })}
            </p>
          </div>

          {/* Nom complet */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.full_name.label")}
            </label>
            <div className="relative">
              <UserRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
              <input
                type="text"
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                className="w-full rounded px-3 py-2 pl-9 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editprofile:fields.full_name.placeholder")}
              />
            </div>
          </div>

          {/* Club */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.club.label")}
            </label>
            <div className="relative">
              <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
              <input
                type="text"
                value={profile.club}
                onChange={(e) => setProfile({ ...profile, club: e.target.value })}
                className="w-full rounded px-3 py-2 pl-9 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editprofile:fields.club.placeholder")}
              />
            </div>
          </div>

          {/* Bio */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.bio.label")}
            </label>
            <div className="relative">
              <FileText className="w-4 h-4 absolute left-3 top-3 text-gray-500 dark:text-gray-400" />
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                rows={3}
                className="w-full rounded px-3 py-2 pl-9 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editprofile:fields.bio.placeholder")}
              />
            </div>
          </div>

          {/* Niveau */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.level.label")}
            </label>
            <select
              value={profile.niveau}
              onChange={(e) => setProfile({ ...profile, niveau: e.target.value })}
              className="w-full rounded px-3 py-2 border
                         bg-white border-gray-300 text-gray-900
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              aria-label={t("editprofile:fields.level.label")}
            >
              <option value="">{t("editprofile:fields.level.placeholder")}</option>
              <option value="Départemental">{t("editprofile:fields.level.options.departemental")}</option>
              <option value="Régional">{t("editprofile:fields.level.options.regional")}</option>
              <option value="National">{t("editprofile:fields.level.options.national")}</option>
              <option value="Elite">{t("editprofile:fields.level.options.elite")}</option>
            </select>
          </div>

          {/* Réalisations */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
              {t("editprofile:fields.achievements.label")}
            </label>
            <div className="relative">
              <Award className="w-4 h-4 absolute left-3 top-3 text-gray-500 dark:text-gray-400" />
              <textarea
                value={profile.achievements}
                onChange={(e) => setProfile({ ...profile, achievements: e.target.value })}
                rows={4}
                className="w-full rounded px-3 py-2 pl-9 border
                           bg-white border-gray-300 text-gray-900 placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400/60
                           dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                placeholder={t("editprofile:fields.achievements.placeholder")}
              />
            </div>
          </div>

          {/* --- RÉSEAUX SOCIAUX --- */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">{t("editprofile:socials.title")}</h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="relative">
                <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={profile.socials.instagram}
                  onChange={(e) => setProfile({ ...profile, socials: { ...profile.socials, instagram: e.target.value } })}
                  placeholder={t("editprofile:socials.instagram_ph")}
                  className="w-full rounded px-3 py-2 pl-9 border bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="relative">
                <AtSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={profile.socials.twitter}
                  onChange={(e) => setProfile({ ...profile, socials: { ...profile.socials, twitter: e.target.value } })}
                  placeholder={t("editprofile:socials.twitter_ph")}
                  className="w-full rounded px-3 py-2 pl-9 border bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="relative">
                <Video className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={profile.socials.youtube}
                  onChange={(e) => setProfile({ ...profile, socials: { ...profile.socials, youtube: e.target.value } })}
                  placeholder={t("editprofile:socials.youtube_ph")}
                  className="w-full rounded px-3 py-2 pl-9 border bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="relative">
                <Video className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={profile.socials.tiktok}
                  onChange={(e) => setProfile({ ...profile, socials: { ...profile.socials, tiktok: e.target.value } })}
                  placeholder={t("editprofile:socials.tiktok_ph")}
                  className="w-full rounded px-3 py-2 pl-9 border bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="relative sm:col-span-2">
                <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={profile.socials.website}
                  onChange={(e) => setProfile({ ...profile, socials: { ...profile.socials, website: e.target.value } })}
                  placeholder={t("editprofile:socials.website_ph")}
                  className="w-full rounded px-3 py-2 pl-9 border bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-orange-400/60
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Link2 className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>

          {/* --- ÉQUIPES PRÉFÉRÉES --- */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">{t("editprofile:favorites.title")}</h2>

            <div className="flex flex-wrap gap-2 mb-3">
              {selectedTeams.length === 0 ? (
                <p className="text-sm text-gray-500">{t("editprofile:favorites.none")}</p>
              ) : (
                selectedTeams.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-2 border rounded-xl px-2 py-1 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                    <img src={logoUrls[t.id]} alt={t.name} className="w-5 h-5 object-contain" />
                    <span className="text-xs">{t.name}</span>
                  </span>
                ))
              )}
            </div>

            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <GridIcon className="w-4 h-4" />
              {t("editprofile:favorites.manage_btn")}
            </button>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full inline-flex items-center justify-center gap-2 font-semibold py-2 px-6 rounded transition
                        focus:outline-none focus:ring-2 focus:ring-orange-400/60
                        ${saving ? "bg-orange-300 cursor-not-allowed text-white" : "bg-[#fc7825] hover:bg-orange-600 text-white"}`}
          >
            <Save className="w-4 h-4" />
            {saving ? t("editprofile:save.saving") : t("editprofile:save.save")}
          </button>
        </div>
      </div>

      {/* Modale */}
      <FavoriteTeamsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        teams={teams}
        logoUrls={logoUrls}
        initialSelected={favoriteTeamIds}
        onApply={(ids) => {
          setFavoriteTeamIds(ids);
          setModalOpen(false);
        }}
        t={t}
      />
    </section>
  );
};
